import { useEffect, useState } from "react";
import api from "../api/axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";

export default function Dashboard() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      totalProperties: 0,
      totalBookings: 0,
      totalRevenue: 0,
      activeBookings: 0,
      pendingBookings: 0,
      availableProperties: 0,
      rentedProperties: 0,
    },
    recentBookings: [],
    monthlyRevenue: []
  });
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get greeting based on time
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return { text: t('good_morning'), icon: 'fa-sun', color: 'from-blue-500 to-blue-600' };
    if (hour < 17) return { text: t('good_afternoon'), icon: 'fa-sun', color: 'from-blue-500 to-blue-600' };
    if (hour < 21) return { text: t('good_evening'), icon: 'fa-cloud-sun', color: 'from-blue-500 to-blue-600' };
    return { text: t('good_night'), icon: 'fa-moon', color: 'from-blue-500 to-blue-600' };
  };

  const greeting = getGreeting();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const timestamp = Date.now();
        const res = await api.get(`/admin/dashboard?_t=${timestamp}`);
        
        if (res.data && res.data.success) {
          setDashboardData({
            stats: res.data.stats || {},
            recentBookings: res.data.recentBookings || [],
            monthlyRevenue: res.data.monthlyRevenue || []
          });
        }
      } catch (err) {
        console.error('Dashboard API Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'bg-gray-900' : ''}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading dashboard...
          </p>
        </div>
      </div>
    );
  }

  const { stats = {}, recentBookings = [] } = dashboardData || {};

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-lg border overflow-hidden`}>
        <div className={`p-6 sm:p-8 ${isDark ? 'bg-gradient-to-r from-gray-700 to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="text-white">
              <div className="flex items-center mb-2">
                <i className={`fas ${greeting.icon} text-3xl mr-3`}></i>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">{greeting.text}!</h1>
                  <p className="text-lg opacity-90">{user?.name || 'Admin'}</p>
                </div>
              </div>
              <p className="text-sm opacity-80 mt-2">
                {currentTime.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 text-white text-right">
              <div className="text-2xl sm:text-3xl font-mono font-bold">
                {currentTime.toLocaleTimeString(language === 'id' ? 'id-ID' : 'en-US', { hour12: false })}
              </div>
              <p className="text-sm opacity-80">{t('real_time')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-6">
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-xl shadow-lg border`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('total_users')}</h4>
            <i className="fas fa-users text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.totalUsers || 0}</p>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-xl shadow-lg border`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('total_properties')}</h4>
            <i className="fas fa-building text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.totalProperties || 0}</p>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-xl shadow-lg border`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('total_bookings')}</h4>
            <i className="fas fa-clipboard-list text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.totalBookings || 0}</p>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-xl shadow-lg border`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('total_revenue')}</h4>
            <i className="fas fa-money-bill-wave text-blue-500" />
          </div>
          <p className="text-xl font-bold text-blue-600">Rp {Number(stats.totalRevenue || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-6">
        {[
          { label: t('active'), value: stats.activeBookings || 0, icon: 'fa-chart-bar', color: 'blue', subtitle: t('bookings') },
          { label: t('pending'), value: stats.pendingBookings || 0, icon: 'fa-clock', color: 'orange', subtitle: t('bookings') },
          { label: t('available'), value: stats.availableProperties || 0, icon: 'fa-check-circle', color: 'green', subtitle: t('properties') },
          { label: t('rented'), value: stats.rentedProperties || 0, icon: 'fa-home', color: 'red', subtitle: t('properties') }
        ].map((stat) => (
          <div
            key={stat.label}
            className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-3 sm:p-4 rounded-xl shadow-lg border hover:shadow-xl transition-shadow`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{stat.label}</h4>
              <i className={`fas ${stat.icon} text-${stat.color}-500`} />
            </div>
            <p className={`text-xl sm:text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{stat.subtitle}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 sm:p-6 rounded-2xl shadow-lg border`}>
          <h2 className={`text-lg sm:text-xl font-bold mb-4 flex items-center ${isDark ? 'text-gray-100' : ''}`}>
            <i className={`fas fa-chart-line mr-2 ${isDark ? 'text-gray-300' : ''}`}></i>
            {t('recent_bookings')}
          </h2>
          <div className="space-y-3">
            {recentBookings.length > 0 ? (
              recentBookings.map((booking) => (
                <div 
                  key={booking.id}
                  className={`flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 rounded-xl transition-shadow shadow-sm hover:shadow-md border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600' 
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  <div className="mb-2 sm:mb-0">
                    <p className={`font-semibold text-sm sm:text-base ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {booking.user?.name || `User #${booking.user_id}`}
                    </p>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {booking.property?.name || `Property #${booking.property_id}`}
                    </p>
                  </div>
                  <div className="flex justify-between sm:block sm:text-right">
                    <p className={`font-semibold text-sm sm:text-base ${isDark ? 'text-gray-200' : 'text-green-600'}`}>
                      Rp {Number(booking.total_amount).toLocaleString()}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                        booking.status === 'active' ? 'bg-green-100 text-green-800' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className={`text-center py-8 text-lg ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <i className={`fas fa-inbox text-4xl mb-3 block ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />
                {t('no_recent_bookings')}
              </p>
            )}
          </div>
        </div>

        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 sm:p-6 rounded-2xl shadow-lg border`}>
          <h2 className={`text-lg sm:text-xl font-bold mb-4 flex items-center ${isDark ? 'text-gray-100' : ''}`}>
            <i className={`fas fa-bolt mr-2 ${isDark ? 'text-gray-300' : 'text-blue-600'}`} />
            {t('quick_actions')}
          </h2>
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
            {[
              { to: '/admin/users', icon: 'fa-user', label: t('manage_users') },
              { to: '/admin/properties', icon: 'fa-building', label: t('properties_management') },
              { to: '/admin/bookings', icon: 'fa-clipboard-list', label: t('view_bookings') },
              { to: '/admin/payments', icon: 'fa-credit-card', label: t('view_payments') }
            ].map((action) => (
              <Link 
                key={action.to}
                to={action.to} 
                className={`block p-3 sm:p-4 rounded-xl text-center transition-shadow shadow-md hover:shadow-lg ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className={`text-xl sm:text-2xl mb-2 ${isDark ? 'text-gray-300' : 'text-blue-600'}`}>
                  <i className={`fas ${action.icon}`}></i>
                </div>
                <div className={`text-xs sm:text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  {action.label}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
